/**
 * News feed (presentational). Ports the legacy "news" subsystem
 * (docs/legacy/AVOID_LEGACY_ANALYSIS.md §2.10) — the original was a single RTDB
 * object hardcoded to exactly 3 slides × 10 languages (a 4th slide failed
 * silently) presented as a NEXT/BACK slideshow. Here we render whatever
 * {@link import('../services/dataModel').NewsItem}s the shell hands us, already
 * sorted by `order`, as an elegant scroll of glamorous glass "slides" so the
 * feed can grow freely.
 *
 * SOURCE: the shell calls `services.getNews()` (a NAMED-ONLY method per
 * src/services/types.ts — it throws {@link import('../services/types').RequiresAccountError}
 * for anonymous users) and passes the result in. This component is purely
 * presentational: it draws `items`, a `loading` skeleton, a `locked` tease for
 * anonymous users, or an empty state. It holds no state and touches no service.
 *
 * ARCHITECTURE: presentational only — no `game`/`services`/Firebase imports
 * beyond the read-only {@link NewsItem} data shape. No per-frame React state.
 */
import type { NewsItem } from '../services/dataModel';
import { useT } from './i18n';

interface NewsScreenProps {
  /** Feed items, expected pre-sorted ascending by `order` (the shell sorts). */
  items: NewsItem[];
  /** True while the shell is fetching the feed. */
  loading?: boolean;
  /**
   * True when the current user is anonymous (the shell caught
   * {@link import('../services/types').RequiresAccountError} from `getNews()`).
   * Shows the "log in to read the news" tease instead of the feed.
   */
  locked?: boolean;
  /** Invoked from the locked tease's CTA — routes to the auth screen. */
  onSignIn?: () => void;
  /** Back to the previous screen (menu / network shell). */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own header/footer-back. */
  embedded?: boolean;
}

/** Human date from epoch ms (locale short date). Defensive against bad values. */
function formatDate(epochMs: number): string {
  if (!Number.isFinite(epochMs) || epochMs <= 0) return '';
  return new Date(epochMs).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function NewsScreen({
  items,
  loading,
  locked,
  onSignIn,
  onBack,
  embedded = false,
}: NewsScreenProps) {
  const t = useT();
  return (
    <div className={'screen news-screen' + (embedded ? ' is-embedded' : '')}>
      <style>{NEWS_CSS}</style>

      {!embedded && (
        <header className="network-head">
          <button className="btn btn-ghost network-back" onClick={onBack}>
            ← Back
          </button>
          <h1 className="network-title">{t('news.title')}</h1>
        </header>
      )}

      <div className="news-intro">
        <p className="subtitle">{t('news.intro')}</p>
      </div>

      {locked ? (
        <section className="panel news-locked u-fade-in">
          <span className="news-lock-badge" aria-hidden="true">
            🔒
          </span>
          <h2>Log in to read the news</h2>
          <p className="news-lock-body">
            The news feed is part of your POPSTAR account. Create one (or log in) to
            see updates, events, and what's coming next.
          </p>
          {onSignIn && (
            <button className="btn btn-primary news-lock-cta" onClick={onSignIn}>
              Log in / Sign up
            </button>
          )}
        </section>
      ) : loading ? (
        <div className="news-list" aria-busy="true" aria-label="Loading news">
          {[0, 1].map((i) => (
            <article key={i} className="panel news-card news-card-skeleton" aria-hidden="true">
              <div className="news-media news-sk-media" />
              <div className="news-body">
                <span className="news-sk-line news-sk-title" />
                <span className="news-sk-line" />
                <span className="news-sk-line" />
                <span className="news-sk-line news-sk-short" />
              </div>
            </article>
          ))}
        </div>
      ) : items.length === 0 ? (
        <section className="panel news-empty u-fade-in">
          <span className="news-empty-glyph" aria-hidden="true">
            📰
          </span>
          <h2>{t('news.empty')}</h2>
          <p className="news-empty-body">{t('news.emptyBody')}</p>
        </section>
      ) : (
        <div className="news-list">
          {items.map((item, i) => {
            const dateLabel = item.publishedAt > 0 ? formatDate(item.publishedAt) : '';
            return (
              <article key={item.id} className="panel news-card u-fade-in">
                {item.imageUrl ? (
                  <div className="news-media">
                    <img
                      className="news-image"
                      src={item.imageUrl}
                      alt=""
                      loading="lazy"
                      // Placeholder art until the CDN pipeline lands: hide a broken
                      // image (and collapse its frame) rather than show the
                      // browser's broken-image glyph.
                      onError={(e) => {
                        const frame = e.currentTarget.parentElement;
                        if (frame) frame.style.display = 'none';
                      }}
                    />
                    <span className="news-media-scrim" aria-hidden="true" />
                    <span className="news-issue" aria-hidden="true">
                      #{String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                ) : null}
                <div className="news-body">
                  {dateLabel && (
                    <time
                      className="news-date"
                      dateTime={new Date(item.publishedAt).toISOString()}
                    >
                      {dateLabel}
                    </time>
                  )}
                  <h2 className="news-title">{item.title}</h2>
                  <p className="news-text">{item.body}</p>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {!embedded && (
        <button className="btn btn-ghost news-foot-back" onClick={onBack}>
          Back
        </button>
      )}
    </div>
  );
}

/**
 * Screen-scoped styles (nested under `.news-screen` so nothing leaks). Shared
 * primitives (`.screen`, `.panel`, `.btn`, `.network-head`/`.network-title`,
 * `.subtitle`, the POPSTAR palette re-map, CSS vars) come from src/index.css;
 * only the news-specific layout lives here. All colours read from the design
 * tokens — no ad-hoc values.
 */
const NEWS_CSS = `
.news-screen { gap: var(--space-4); }

.news-screen .network-head { margin-top: 2vh; }

.news-screen .news-intro {
  width: min(440px, 92vw);
  text-align: center;
}
.news-screen .news-intro .subtitle {
  margin: 0;
  line-height: 1.5;
}

/* ---- Feed ---- */
.news-screen .news-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: min(440px, 92vw);
}

/* Each slide is a frosted glass card. Padding 0 so the image bleeds to the
   rounded edge; the body re-introduces its own padding. */
.news-screen .news-card {
  width: 100%;
  padding: 0;
  overflow: hidden;
  transition: transform var(--t) var(--ease), box-shadow var(--t) var(--ease),
    border-color var(--t) var(--ease);
}
.news-screen .news-card:hover {
  transform: translateY(-2px);
  border-color: var(--glass-border-strong);
  box-shadow: var(--shadow-2), inset 0 1px 0 var(--glass-hi);
}

/* ---- Media (image + scrim + issue chip) ---- */
.news-screen .news-media {
  position: relative;
  width: 100%;
  height: 180px;
  background: var(--panel-2);
}
.news-screen .news-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
/* Soft gradient so the rounded top edge and any chip stay legible over art. */
.news-screen .news-media-scrim {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(18, 8, 34, 0.32) 0%,
    transparent 38%,
    rgba(18, 8, 34, 0.5) 100%
  );
}
.news-screen .news-issue {
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--gold);
  background: var(--panel-strong);
  border: 1px solid rgba(255, 209, 102, 0.3);
  border-radius: var(--radius-pill);
  padding: 4px 11px;
  font-variant-numeric: tabular-nums;
  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));
}

/* ---- Body ---- */
.news-screen .news-body { padding: 16px 18px 18px; }
.news-screen .news-date {
  display: block;
  margin: 0 0 8px;
  font-size: 11px;
  color: var(--accent-soft);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 700;
}
.news-screen .news-title {
  margin: 0 0 8px;
  font-size: 19px;
  line-height: 1.25;
  text-transform: none;
  letter-spacing: 0;
  color: var(--text);
}
.news-screen .news-text {
  margin: 0;
  color: var(--text-dim);
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-line;
}

/* ---- Locked tease ---- */
.news-screen .news-locked {
  width: min(440px, 92vw);
  text-align: center;
  padding: 28px 24px;
}
.news-screen .news-lock-badge {
  display: inline-grid;
  place-items: center;
  width: 64px;
  height: 64px;
  margin: 0 auto 14px;
  font-size: 30px;
  border-radius: var(--radius);
  background: linear-gradient(135deg, var(--accent-tint), var(--panel-2));
  border: 1px solid var(--glass-border-strong);
  box-shadow: inset 0 1px 0 var(--glass-hi);
}
.news-screen .news-locked h2 {
  text-transform: none;
  letter-spacing: 0;
  color: var(--text);
  font-size: 20px;
  margin: 0 0 8px;
}
.news-screen .news-lock-body {
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
  margin: 0 auto 18px;
  max-width: 34ch;
}
.news-screen .news-lock-cta { width: 100%; }

/* ---- Empty state ---- */
.news-screen .news-empty {
  width: min(440px, 92vw);
  text-align: center;
  padding: 32px 24px;
}
.news-screen .news-empty-glyph { font-size: 38px; display: block; margin-bottom: 10px; }
.news-screen .news-empty h2 {
  text-transform: none;
  letter-spacing: 0;
  color: var(--text);
  font-size: 19px;
  margin: 0 0 6px;
}
.news-screen .news-empty-body {
  color: var(--muted);
  font-size: 14px;
  margin: 0;
}

/* ---- Loading skeleton (mirrors the card shape; shimmer respects reduced-motion) ---- */
.news-screen .news-card-skeleton { pointer-events: none; }
.news-screen .news-sk-media { height: 180px; }
.news-screen .news-sk-media,
.news-screen .news-sk-line {
  position: relative;
  overflow: hidden;
  background: var(--panel-2);
}
.news-screen .news-sk-line {
  display: block;
  height: 12px;
  border-radius: var(--radius-pill);
  margin-bottom: 10px;
}
.news-screen .news-sk-line:last-child { margin-bottom: 0; }
.news-screen .news-sk-title {
  height: 18px;
  width: 70%;
  margin-bottom: 14px;
}
.news-screen .news-sk-short { width: 45%; }
.news-screen .news-sk-media::after,
.news-screen .news-sk-line::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    var(--glass-hi),
    transparent
  );
  animation: news-shimmer 1.4s ease-in-out infinite;
}

/* ---- Footer back (secondary; the header back is primary nav) ---- */
.news-screen .news-foot-back { margin-top: 4px; }

@keyframes news-shimmer {
  to { transform: translateX(100%); }
}
@media (prefers-reduced-motion: reduce) {
  .news-screen .news-sk-media::after,
  .news-screen .news-sk-line::after { animation: none; }
}

@media (max-width: 380px) {
  .news-screen .news-media,
  .news-screen .news-sk-media { height: 150px; }
  .news-screen .news-title { font-size: 18px; }
}
`;
